import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useCompanyName } from "@/lib/company";
import { Page, PageHeader, Loading, Tag } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import { OwedBanner } from "@/components/OwedBanner";
import SendDocument from "@/components/SendDocument";
import { useAssistant } from "@/lib/assistant";
import { cn } from "@/lib/utils";
import {
  TYPES, STATUSES, MAIN_ACTION, docName, docSub, periodLabel, statusWord, preparedLabel, keptLabel, allowed, headline,
  RULE_NAME, ruleLine, zonedStamp,
} from "@/lib/documents";
import {
  CircleCheck, LoaderCircle, Send, Clock, CirclePause, TriangleAlert, Download, Copy, Trash2, Lock, FileText,
  Ellipsis, CalendarClock,
} from "lucide-react";

// design_handoff_founder_assistant/screens/Documents.dc.html (screen 3): everything the founder's assistant prepared,
// newest first, with its status and what can be done with it, and the scheduled investor updates (backend/documents.py,
// FEATURE_DOCUMENTS). Founders only; kept 90 days. A table from 1280px, cards below it (the phone layout).
// The header's Assistant (every founder page has it: PageHeader) and the empty state's Open the assistant open the
// assistant beside it (components/Assistant.jsx).
// Send and Send again open the send dialog (components/SendDocument.jsx); Schedule an investor update and a schedule's
// Edit open the scheduler (Scheduler.jsx), and each active schedule's next update shows in the list as Scheduled.
// Preview opens DocumentPreview.jsx.
const STATUS_LOOK = {
  preparing: [LoaderCircle, "text-gr-ink-secondary", "motion-safe:animate-spin"],
  ready: [CircleCheck, "text-gr-success"],
  sent: [Send, "text-gr-ink-secondary"],
  scheduled: [Clock, "text-foreground"],
  held: [CirclePause, "text-gr-warning"],
  nocheck: [TriangleAlert, "text-gr-danger"],
  failed: [TriangleAlert, "text-gr-danger"],
};
// every row is its own grid, so each column has a fixed or fractional width (an auto one would differ row to row)
const COLS = "grid grid-cols-[minmax(160px,2fr)_minmax(96px,1fr)_minmax(150px,1.4fr)_132px_300px] gap-4 items-center px-5 py-2.5";
const UPPER = "text-xs uppercase tracking-[0.09em] text-muted-foreground";

export function DocStatus({ doc }) {
  const [Icon, tone, spin] = STATUS_LOOK[doc.status] || [Clock, "text-foreground"];
  return (
    <span className={cn("inline-flex items-start gap-1.5 font-semibold", tone)} data-testid="document-status">
      <Icon className={cn("w-3.5 h-3.5 shrink-0 mt-[3px]", spin)} aria-hidden="true" />{statusWord(doc)}
    </span>
  );
}

// the whole page's file actions: Download PDF saves it, Copy email puts the email on the clipboard to paste into the
// founder's own email app
export function useFiles(company) {
  const fileName = (doc) => [company, docName(doc), periodLabel(doc.period)].filter(Boolean).join(" - ").replace(/[\\/:*?"<>|]/g, "");
  const download = useMutation({
    mutationFn: async (doc) => ({ doc, blob: (await api.get(`/documents/${doc.id}/files/pdf`, { responseType: "blob" })).data }),
    onSuccess: ({ doc, blob }) => {
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement("a"), { href: url, download: `${fileName(doc)}.pdf` });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    onError: async (e) => toast.error(await blobErr(e)),
  });
  const copy = useMutation({
    mutationFn: async (doc) => {
      const html = (await api.get(`/documents/${doc.id}/files/email`, { responseType: "text" })).data;
      const text = new DOMParser().parseFromString(html, "text/html").body.innerText;
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([text], { type: "text/plain" }) })]);
      } else {
        await navigator.clipboard.writeText(text);
      }
    },
    onSuccess: () => toast.success("Email copied. Paste it into your email app."),
    onError: async (e) => toast.error(await blobErr(e)),
  });
  return { download, copy };
}

// an error answered to a blob request carries its detail as a blob
async function blobErr(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).detail || apiErr(e); } catch { return apiErr(e); }
  }
  return apiErr(e);
}

function PreviewButton({ doc, className }) {
  if (!allowed(doc).preview) return <Button variant="ghost" disabled className={className}>Preview</Button>;
  return (
    <Button variant="ghost" asChild className={className}>
      <Link to={`/founder/documents/${doc.id}`} data-testid="document-preview">Preview</Link>
    </Button>
  );
}

// the status's main action; `label` names another (a held update's Send now, once credits came), `also` adds one (Skip)
function MainAction({ doc, actions, className }) {
  const act = actions[doc.status];
  if (!act) return null;  // its part isn't built yet
  const can = allowed(doc).main;
  const label = act.label || MAIN_ACTION[doc.status];
  const props = { variant: "outline", disabled: !can, className, "data-testid": "document-main" };
  const main = act.to ? <Button {...props} asChild={can}>{can ? <Link to={act.to}>{label}</Link> : label}</Button>
    : <Button {...props} onClick={() => act.run(doc)}>{label}</Button>;
  if (!act.also) return main;
  return (<>
    <Button variant="ghost" className={className} onClick={() => act.also.run(doc)} data-testid="document-also">{act.also.label}</Button>
    {main}
  </>);
}

function Row({ doc, actions, files, onDelete }) {
  const can = allowed(doc);
  const kept = keptLabel(doc);
  return (
    <div role="row" className={cn(COLS, "border-t tabular-nums")} data-testid="document-row">
      <span role="cell" className="flex flex-col min-w-0">
        <span className="font-semibold">{docName(doc)}</span>
        <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{docSub(doc)}</span>
      </span>
      <span role="cell">{periodLabel(doc.period)}</span>
      <span role="cell"><DocStatus doc={doc} /></span>
      <span role="cell" className="flex flex-col">
        <span>{preparedLabel(doc)}</span>
        <span className={cn("text-sm", kept.warn ? "text-gr-warning" : "text-muted-foreground")}>{kept.text}</span>
      </span>
      <span role="cell" className="flex items-center justify-end gap-1">
        <PreviewButton doc={doc} className="px-2.5" />
        <MainAction doc={doc} actions={actions} className="px-2.5" />
        <Button variant="ghost" size="icon" aria-label="Download PDF" title="Download PDF" disabled={!can.pdf || files.download.isPending}
          onClick={() => files.download.mutate(doc)} data-testid="document-pdf"><Download /></Button>
        <Button variant="ghost" size="icon" aria-label="Copy email" title="Copy email" disabled={!can.copy || files.copy.isPending}
          onClick={() => files.copy.mutate(doc)} data-testid="document-copy"><Copy /></Button>
        <Button variant="danger" size="icon" aria-label="Delete" title="Delete" onClick={() => onDelete(doc)}
          data-testid="document-delete"><Trash2 /></Button>
      </span>
    </div>
  );
}

// the phone layout: a card per document, its actions 44px tall, the rest under More actions
function DocCard({ doc, actions, files, onDelete }) {
  const can = allowed(doc);
  const kept = keptLabel(doc);
  const prepared = preparedLabel(doc);
  return (
    <li className="bg-card rounded-[10px] px-3.5 py-3 flex flex-col gap-1.5 tabular-nums" data-testid="document-card">
      <span className="flex justify-between gap-2">
        <span className="font-semibold">{docName(doc)}</span>
        <span className="text-muted-foreground text-end">{periodLabel(doc.period)}</span>
      </span>
      <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{docSub(doc)}</span>
      <DocStatus doc={doc} />
      {!(prepared === "—" && kept.text === "—") && (  // a scheduled one: nothing prepared or kept yet
        <span className="text-sm text-muted-foreground">
          {prepared} · <span className={kept.warn ? "text-gr-warning" : undefined}>{kept.text}</span>
        </span>
      )}
      <span className="flex items-center gap-1 mt-1">
        <PreviewButton doc={doc} className="h-11 px-3" />
        <MainAction doc={doc} actions={actions} className="h-11 px-3" />
        <span className="flex-1" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-11 w-11 p-0" aria-label="More actions: Download PDF, Copy email, Delete"
              data-testid="document-more"><Ellipsis /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={!can.pdf} onSelect={() => files.download.mutate(doc)}><Download />Download PDF</DropdownMenuItem>
            <DropdownMenuItem disabled={!can.copy} onSelect={() => files.copy.mutate(doc)}><Copy />Copy email</DropdownMenuItem>
            <DropdownMenuItem className="text-gr-danger focus:text-gr-danger" onSelect={() => onDelete(doc)}><Trash2 />Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </span>
    </li>
  );
}

function Filter({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col gap-1 text-sm text-muted-foreground">
      {label}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label} className="w-auto min-w-[180px] text-foreground" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map(([v, text]) => <SelectItem key={v} value={v}>{text}</SelectItem>)}</SelectContent>
      </Select>
    </label>
  );
}

function DocumentsList({ docs, actions, files, onDelete }) {
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const shown = docs.filter((d) => (type === "all" || d.type === type) && (status === "all" || d.status === status));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Filter label="Type" value={type} onChange={setType} options={[["all", "All types"], ...TYPES]} testid="documents-type" />
        <Filter label="Status" value={status} onChange={setStatus} options={[["all", "All statuses"], ...STATUSES]} testid="documents-status" />
        <span className="ms-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <Lock className="w-3 h-3" aria-hidden="true" />Only founders see documents. Kept 90 days, then deleted.
        </span>
      </div>
      {shown.length === 0 ? (
        <p className="m-0 bg-card rounded-[10px] px-5 py-6 text-muted-foreground" data-testid="documents-none-match">No documents match these filters.</p>
      ) : (<>
        <div role="table" aria-label="Documents, newest first" className="hidden xl:flex flex-col bg-card rounded-[10px] overflow-hidden">
          <div role="row" className={cn(COLS, UPPER)}>
            <span role="columnheader">Document</span><span role="columnheader">Period</span><span role="columnheader">Status</span>
            <span role="columnheader">Prepared · Kept</span><span role="columnheader" className="text-end">Actions</span>
          </div>
          {shown.map((d) => <Row key={d.id} doc={d} actions={actions} files={files} onDelete={onDelete} />)}
        </div>
        <ul aria-label="Documents, newest first" className="xl:hidden m-0 p-0 list-none flex flex-col gap-2">
          {shown.map((d) => <DocCard key={d.id} doc={d} actions={actions} files={files} onDelete={onDelete} />)}
        </ul>
      </>)}
    </div>
  );
}

function EmptyDocuments() {
  const { setOpen } = useAssistant();
  return (
    <div className="bg-card rounded-[10px] px-6 py-12 flex flex-col items-center gap-3 text-center" data-testid="documents-empty">
      <span className="w-10 h-10 rounded-[10px] bg-gr-surface-2 grid place-items-center"><FileText className="w-[18px] h-[18px]" aria-hidden="true" /></span>
      <p className="m-0 font-display text-[18px] leading-6 font-semibold">Ask the assistant to prepare your first document.</p>
      <p className="m-0 max-w-[440px] text-muted-foreground">
        Monthly money sheet, weekly founder report, investor update or objective brief, in your company's brand.
      </p>
      <Button className="mt-1" onClick={() => setOpen(true)} data-testid="documents-open-assistant">Open the assistant</Button>
    </div>
  );
}

// the Scheduled tab. Its rows (each schedule's rule, next date and people, with Edit, Pause and Delete) come with the
// investor update scheduler (part 4); until then nothing can be scheduled
// the Scheduled tab: each schedule with its rule, its next date and who gets it; Edit opens the scheduler (Scheduler.jsx)
const SCOLS = "grid grid-cols-[minmax(180px,2fr)_150px_minmax(140px,1.2fr)_250px] gap-4 items-center px-5 py-3";

function Scheduled({ schedules, onToggle, onDelete, pending }) {
  if (!schedules.length) return (
    <div className="flex flex-col gap-3" data-testid="documents-scheduled">
      <p className="m-0 bg-card rounded-[10px] px-5 py-6 text-muted-foreground" data-testid="schedules-empty">No investor updates scheduled.</p>
      <span className="text-sm text-muted-foreground">Investors also get the Monday summary unless they turned it off.</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-3" data-testid="documents-scheduled">
      <div role="table" aria-label="Scheduled investor updates" className="bg-card rounded-[10px] overflow-x-auto">
        <div role="row" className={cn(SCOLS, "min-w-[760px]", UPPER)}>
          <span role="columnheader">Schedule</span><span role="columnheader">Next</span><span role="columnheader">To</span>
          <span role="columnheader" className="text-end">Actions</span>
        </div>
        {schedules.map((s) => {
          const to = s.recipients.filter((r) => !r.stopped).map((r) => r.name || r.email);
          return (
            <div key={s.id} role="row" className={cn(SCOLS, "min-w-[760px] border-t")} data-testid="schedule-row">
              <span role="cell" className="flex flex-col min-w-0">
                <span className="flex items-center gap-2 font-semibold">Investor update
                  <Tag tone={s.paused ? "muted" : "info"}>{s.paused ? "Paused" : "Active"}</Tag></span>
                <span className="text-sm text-muted-foreground">{ruleLine(s)}</span>
              </span>
              <span role="cell" className="tabular-nums">{s.paused || !s.next_at ? "Paused" : zonedStamp(s.next_at, s.tz)}</span>
              <span role="cell" className="min-w-0 [overflow-wrap:anywhere]">{to.join(", ") || "Nobody"}</span>
              <span role="cell" className="flex items-center justify-end gap-1">
                <Button variant="ghost" asChild><Link to={`/founder/documents/schedule/${s.id}`} data-testid="schedule-edit">Edit</Link></Button>
                <Button variant="outline" disabled={pending} onClick={() => onToggle(s)} data-testid="schedule-toggle">{s.paused ? "Resume" : "Pause"}</Button>
                <Button variant="danger" size="icon" aria-label={`Delete the ${RULE_NAME[s.rule][0].toLowerCase()}${RULE_NAME[s.rule].slice(1)} investor update`}
                  title="Delete" onClick={() => onDelete(s)} data-testid="schedule-delete"><Trash2 /></Button>
              </span>
            </div>
          );
        })}
      </div>
      <span className="text-sm text-muted-foreground">Investors also get the Monday summary unless they turned it off.</span>
    </div>
  );
}

function Tab({ on, onClick, children, testid }) {
  return (
    <button type="button" role="tab" aria-selected={on} onClick={onClick} data-testid={testid}
      className={cn("h-9 px-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
        on ? "font-semibold shadow-[inset_0_-2px_0_hsl(var(--foreground))]" : "text-muted-foreground hover:text-foreground")}>
      {children}
    </button>
  );
}

export default function Documents() {
  const qc = useQueryClient();
  const company = useCompanyName();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "scheduled" ? "scheduled" : "documents";
  const setTab = (t) => setParams(t === "scheduled" ? { tab: t } : {}, { replace: true });
  const { data, isLoading } = useQuery({
    queryKey: ["documents"], queryFn: async () => (await api.get("/documents")).data,
    // while one is being prepared, its status moves on its own
    refetchInterval: (q) => (q.state.data?.documents || []).some((d) => d.status === "preparing") ? 5000 : false,
  });
  const files = useFiles(company);
  const [deleting, setDeleting] = useState(null);
  // a document, or a schedule (from the Scheduled tab, or its Scheduled row in the list)
  const scheduleOf = (x) => x && (x.schedule_id && x.status === "scheduled" ? x.schedule_id : x.rule && !x.status ? x.id : null);
  const remove = useMutation({
    mutationFn: async ({ doc, password }) => (scheduleOf(doc)
      ? api.delete(`/documents/schedules/${scheduleOf(doc)}`, { data: { password } })
      : api.delete(`/documents/${doc.id}`, { data: { password } })),
    onSuccess: (_d, { doc }) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      setDeleting(null);
      toast.success(scheduleOf(doc) ? "Schedule deleted." : "Document deleted.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  // Pause keeps a schedule and sends nothing; Resume picks it up from now
  const toggle = useMutation({
    mutationFn: async (s) => (await api.post(`/documents/schedules/${s.id}/${s.paused ? "resume" : "pause"}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
    onError: (e) => toast.error(apiErr(e)),
  });
  // Try again: prepared once more with fresh numbers (backend doc_jobs.py)
  const retry = useMutation({
    mutationFn: async (doc) => (await api.post(`/documents/${doc.id}/retry`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
    onError: (e) => toast.error(apiErr(e)),
  });
  // Send and Send again open the send dialog (components/SendDocument.jsx)
  const [sending, setSending] = useState(null);
  // a held update (no credits when it was due): Send now prepares it again and sends it, Skip drops it
  const held = useMutation({
    mutationFn: async ({ doc, how }) => (await api.post(`/documents/${doc.id}/${how}`)).data,
    onSuccess: (_d, { how }) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success(how === "skip" ? "Skipped. Nothing was sent." : "Being prepared. It goes out as soon as it's ready.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  // each status's main action, as far as it's built: the rest come with their parts
  const actions = {
    ready: { run: (doc) => setSending(doc) },
    sent: { run: (doc) => setSending(doc) },
    scheduled: { run: () => setTab("scheduled") },
    held: data?.credits_ok
      ? { label: "Send now", run: (doc) => held.mutate({ doc, how: "send-now" }), also: { label: "Skip", run: (doc) => held.mutate({ doc, how: "skip" }) } }
      : { to: "/founder/store#topup" },
    nocheck: { run: (doc) => retry.mutate(doc) },
    failed: { run: (doc) => retry.mutate(doc) },
  };
  const eyebrow = [company, "Documents"].filter(Boolean).join(" · ");
  const title = "Everything your assistant prepared.";
  const schedule = (
    <Button asChild data-testid="documents-schedule"><Link to="/founder/documents/schedule"><CalendarClock />Schedule an investor update</Link></Button>
  );
  if (isLoading || !data) return <Page className="gap-5"><PageHeader eyebrow={eyebrow} title={title} actions={schedule} /><Loading /></Page>;
  const docs = data.documents;
  return (
    <Page className="gap-5" data-testid="owner-documents">
      <PageHeader eyebrow={eyebrow} title={title} status={headline(docs)} actions={schedule} testid="documents-header" />
      <OwedBanner owed={data.owed_usd} />
      <div role="tablist" aria-label="Documents" className="flex gap-1 border-b">
        <Tab on={tab === "documents"} onClick={() => setTab("documents")} testid="documents-tab-documents">
          Documents <span className="text-muted-foreground font-normal">{docs.length}</span>
        </Tab>
        <Tab on={tab === "scheduled"} onClick={() => setTab("scheduled")} testid="documents-tab-scheduled">
          Scheduled <span className="text-muted-foreground font-normal">{data.schedules.length}</span>
        </Tab>
      </div>
      {tab === "scheduled" ? <Scheduled schedules={data.schedules} onToggle={(s) => toggle.mutate(s)} onDelete={setDeleting} pending={toggle.isPending} />
        : docs.length === 0 ? <EmptyDocuments />
        : <DocumentsList docs={docs} actions={actions} files={files} onDelete={setDeleting} />}
      <SendDocument doc={sending} open={!!sending} onOpenChange={(v) => !v && setSending(null)} />
      <ConfirmDestructive open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}
        title={scheduleOf(deleting) ? "Delete this schedule?" : "Delete this document?"}
        action="Delete" pending={remove.isPending} onConfirm={(password) => remove.mutate({ doc: deleting, password })}>
        {deleting && (scheduleOf(deleting)
          ? <p className="m-0 [text-wrap:pretty]">This investor update stops for good: nothing more is prepared or sent. Updates it
            already prepared stay in Documents.</p>
          : <p className="m-0 [text-wrap:pretty]">{docName(deleting)}, {periodLabel(deleting.period)}, and its files are deleted
            for good. Whoever it was sent to keeps their copy.</p>)}
      </ConfirmDestructive>
    </Page>
  );
}
